import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useEmployeeFaltasHooks } from './hooks';
import { EmployeeFaltaFormDialog } from './EmployeeFaltaFormDialog';
import type { EmployeeFaltaDto, EmployeeFaltaListItem } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataFalta',
  sortOrder: 'desc',
};

export interface EmployeeFaltasManagerProps {
  employeeId: number;
}

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

/**
 * CRUD reusável de Faltas de um Colaborador. Mesmo pattern de
 * `EmployeeTaxasManager`. PK do filho é `dataFalta` (string).
 */
export function EmployeeFaltasManager({ employeeId }: EmployeeFaltasManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('colaborador-faltas', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<EmployeeFaltaDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<EmployeeFaltaListItem | null>(null);

  const hooks = useEmployeeFaltasHooks(employeeId);
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();

  async function openEdit(row: EmployeeFaltaListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(row.dataFalta));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.dataFalta);
      toast.success(t('features.colaboradores.faltas.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<EmployeeFaltaListItem>[] = [
    {
      field: 'dataFalta',
      headerName: t('features.colaboradores.faltas.columns.dataFalta'),
      width: 140,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'justificada',
      headerName: t('features.colaboradores.faltas.columns.justificada'),
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.row.justificada
              ? t('features.colaboradores.faltas.justificada.yes')
              : t('features.colaboradores.faltas.justificada.no')
          }
          color={params.row.justificada ? 'success' : 'default'}
          variant={params.row.justificada ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'motivo',
      headerName: t('features.colaboradores.faltas.columns.motivo'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.colaboradores.faltas.actions.edit', {
                data: formatDate(params.row.dataFalta),
              })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.colaboradores.faltas.actions.delete', {
                data: formatDate(params.row.dataFalta),
              })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.colaboradores.faltas.actions.new')}
        </Button>
      </Stack>

      <DataTable<EmployeeFaltaListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.colaboradores.faltas.searchPlaceholder')}
      />

      <EmployeeFaltaFormDialog
        open={editing !== null}
        falta={editing === 'new' ? null : editing}
        hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.faltas.deleteConfirm.title')}
        description={t('features.colaboradores.faltas.deleteConfirm.description', {
          data: pendingDelete ? formatDate(pendingDelete.dataFalta) : '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
